"use client";

import React from "react";
import { Button } from "@os/components/ui/button";
import { Save as SaveIcon, Loader2, CheckCircle2, FileText, MapPin } from "lucide-react";

type MapleSheetStatusBarProps = {
  selectedCell: string;
  sheetName: string;
  currentFilePath?: string;
  nonEmptyCells: number;
  isSaving: boolean;
  autoSaving: boolean;
  isSaved: boolean;
  onSave: () => void;
};

const MapleSheetStatusBar: React.FC<MapleSheetStatusBarProps> = ({
  selectedCell,
  sheetName,
  currentFilePath,
  nonEmptyCells,
  isSaving,
  autoSaving,
  isSaved,
  onSave,
}) => {
  const saving = isSaving || autoSaving;
  const fileLabel =
    currentFilePath?.split("\\").pop() ||
    sheetName ||
    "Untitled Spreadsheet";
  const folderLabel =
    currentFilePath?.replace(/\\[^\\]+$/, "") || "C:\\";

  return (
    <div className="border-t border-border bg-muted/40 backdrop-blur-sm px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
      <div className="flex items-center gap-3 overflow-hidden">
        <span className="flex items-center gap-1 min-w-0">
          <FileText className="h-4 w-4 text-foreground/70 shrink-0" />
          <span className="font-medium text-foreground truncate" title={fileLabel}>
            {fileLabel}
          </span>
        </span>
        <span className="text-muted-foreground">•</span>
        <span className="flex items-center gap-1 min-w-0">
          <MapPin className="h-4 w-4 text-foreground/50 shrink-0" />
          <span className="truncate" title={folderLabel}>
            {folderLabel}
          </span>
        </span>
        <span className="text-muted-foreground">•</span>
        <span className="font-medium text-foreground">
          Selected: {selectedCell}
        </span>
        <span className="text-muted-foreground">•</span>
        <span className="font-medium text-foreground">
          Cells: {nonEmptyCells}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {saving ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-blue-600">{isSaving ? "Saving…" : "Autosaving…"}</span>
          </div>
        ) : isSaved ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-green-600">Saved</span>
          </div>
        ) : (
          <span className="text-muted-foreground">Editing</span>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={onSave}
          disabled={isSaving}
          className="h-7 px-2"
          title="Save (Ctrl+S)"
        >
          <SaveIcon className="h-4 w-4 mr-1" />
          Save
        </Button>
      </div>
    </div>
  );
};

export default MapleSheetStatusBar;